import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { FolderOpen, Upload, Search, File, Image, FileText, Download, Trash2, Plus, Grid, List, HardDrive, Shield, X, ChevronRight, FolderPlus } from 'lucide-react';

interface FileItem {
  id: string;
  name: string;
  type: 'image' | 'pdf' | 'doc' | 'video';
  size: string;
  folder: string;
  uploadedAt: string;
  uploadedBy: string;
  tags: string[];
}

interface Folder {
  id: string;
  name: string;
  count: number;
  color: string;
  icon: typeof FolderOpen;
}

const folders: Folder[] = [
  { id: 'photos', name: 'Installation Photos', count: 248, color: 'from-blue-400 to-blue-600', icon: Image },
  { id: 'invoices', name: 'Invoices', count: 156, color: 'from-green-400 to-green-600', icon: FileText },
  { id: 'warranty', name: 'Warranty Certificates', count: 89, color: 'from-violet-400 to-violet-600', icon: Shield },
  { id: 'contracts', name: 'Contracts', count: 43, color: 'from-amber-400 to-amber-600', icon: File },
  { id: 'drawings', name: 'Floor Plans & Drawings', count: 67, color: 'from-pink-400 to-rose-600', icon: Grid },
  { id: 'reports', name: 'Reports', count: 34, color: 'from-indigo-400 to-indigo-600', icon: FileText },
];

const mockFiles: FileItem[] = [
  { id: '1', name: 'AIA_Insurance_Installation_Before.jpg', type: 'image', size: '2.4 MB', folder: 'photos', uploadedAt: '2026-07-10', uploadedBy: 'Kamal Perera', tags: ['before', 'AIA', 'CCTV'] },
  { id: '2', name: 'AIA_Insurance_Installation_After.jpg', type: 'image', size: '3.1 MB', folder: 'photos', uploadedAt: '2026-07-10', uploadedBy: 'Kamal Perera', tags: ['after', 'AIA', 'CCTV'] },
  { id: '3', name: 'INV-2026-047.pdf', type: 'pdf', size: '456 KB', folder: 'invoices', uploadedAt: '2026-07-09', uploadedBy: 'System', tags: ['invoice', 'AIA'] },
  { id: '4', name: 'WARRANTY-2026-012.pdf', type: 'pdf', size: '312 KB', folder: 'warranty', uploadedAt: '2026-07-08', uploadedBy: 'System', tags: ['warranty', 'Hikvision'] },
  { id: '5', name: 'Hotel_Reef_Network_Diagram.pdf', type: 'pdf', size: '1.8 MB', folder: 'drawings', uploadedAt: '2026-07-07', uploadedBy: 'Nimal Silva', tags: ['network', 'Hotel Reef'] },
  { id: '6', name: 'MAS_Holdings_Contract.pdf', type: 'pdf', size: '890 KB', folder: 'contracts', uploadedAt: '2026-07-06', uploadedBy: 'Admin', tags: ['contract', 'MAS', 'annual'] },
  { id: '7', name: 'Q2_2026_Performance_Report.pdf', type: 'pdf', size: '2.2 MB', folder: 'reports', uploadedAt: '2026-07-01', uploadedBy: 'Admin', tags: ['report', 'Q2', '2026'] },
  { id: '8', name: 'Dialog_CCTV_Floor_Plan.jpg', type: 'image', size: '4.5 MB', folder: 'drawings', uploadedAt: '2026-06-28', uploadedBy: 'Dilshan Fernando', tags: ['floorplan', 'Dialog', 'CCTV'] },
];

const typeIcons: Record<string, typeof File> = {
  image: Image,
  pdf: FileText,
  doc: FileText,
  video: File,
};
const typeColors: Record<string, string> = {
  image: 'text-blue-500 bg-blue-50',
  pdf: 'text-red-500 bg-red-50',
  doc: 'text-indigo-500 bg-indigo-50',
  video: 'text-amber-500 bg-amber-50',
};

export default function FileManagementPage() {
  const [activeFolder, setActiveFolder] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const filteredFiles = mockFiles.filter(f => {
    const matchFolder = !activeFolder || f.folder === activeFolder;
    const matchSearch = !searchQuery || f.name.toLowerCase().includes(searchQuery.toLowerCase()) || f.tags.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchFolder && matchSearch;
  });

  const totalSize = '14.8 GB';
  const usedPercent = 42;

  return (
    <div className="h-full overflow-y-auto bg-[#FAFAFA] p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-amber-500 to-[#658C53] flex items-center justify-center shadow-lg shadow-amber-200">
            <FolderOpen className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">File Management</h1>
            <p className="text-xs text-gray-500">Photos · Invoices · Warranty · Contracts · Drawings</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2 bg-gradient-to-r from-amber-500 to-[#658C53] text-white text-sm font-medium px-4 py-2.5 rounded-xl hover:shadow-lg hover:shadow-amber-200 transition-all">
            <Upload className="w-4 h-4" /> Upload Files
          </button>
          <input ref={fileInputRef} type="file" multiple className="hidden" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="space-y-4">
          {/* Storage */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <HardDrive className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-semibold text-gray-900">Cloud Storage</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-2">
              <div className="h-full bg-gradient-to-r from-amber-400 to-[#7FA86A] rounded-full" style={{ width: `${usedPercent}%` }} />
            </div>
            <p className="text-xs text-gray-500">{totalSize} of 35 GB used ({usedPercent}%)</p>
            <div className="mt-3 space-y-1.5">
              {[['Images', '8.2 GB', 'bg-blue-400'], ['Documents', '4.5 GB', 'bg-red-400'], ['Videos', '2.1 GB', 'bg-amber-400']].map(([label, size, color]) => (
                <div key={label as string} className="flex items-center gap-2 text-xs">
                  <div className={`w-2 h-2 rounded-full ${color}`} />
                  <span className="text-gray-600 flex-1">{label}</span>
                  <span className="text-gray-400 font-medium">{size}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Folders */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-gray-900">Folders</span>
              <button className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                <FolderPlus className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div className="space-y-1">
              <button
                onClick={() => setActiveFolder(null)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition-colors ${!activeFolder ? 'bg-amber-50 text-amber-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
              >
                <FolderOpen className="w-4 h-4" /> All Files
                <span className="ml-auto text-xs text-gray-400">{mockFiles.length}</span>
              </button>
              {folders.map(folder => {
                const Icon = folder.icon;
                return (
                  <button
                    key={folder.id}
                    onClick={() => setActiveFolder(folder.id)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition-colors ${activeFolder === folder.id ? 'bg-amber-50 text-amber-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="flex-1 text-left truncate">{folder.name}</span>
                    <span className="text-xs text-gray-400">{folder.count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          {/* Toolbar */}
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search files, tags…"
                className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-400 bg-white"
              />
            </div>
            <div className="flex items-center bg-white border border-gray-200 rounded-xl overflow-hidden">
              <button onClick={() => setViewMode('list')} className={`p-2.5 transition-colors ${viewMode === 'list' ? 'bg-amber-50 text-amber-600' : 'text-gray-400 hover:text-gray-600'}`}>
                <List className="w-4 h-4" />
              </button>
              <button onClick={() => setViewMode('grid')} className={`p-2.5 transition-colors ${viewMode === 'grid' ? 'bg-amber-50 text-amber-600' : 'text-gray-400 hover:text-gray-600'}`}>
                <Grid className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Drop Zone */}
          <div
            onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={e => { e.preventDefault(); setIsDragging(false); }}
            className={`border-2 border-dashed rounded-2xl py-5 mb-4 text-center transition-all ${isDragging ? 'border-amber-400 bg-amber-50' : 'border-gray-200 bg-white hover:border-amber-300 hover:bg-amber-50/30'}`}
          >
            <Upload className="w-6 h-6 text-gray-300 mx-auto mb-1" />
            <p className="text-sm text-gray-400">Drop files here or <span className="text-amber-600 font-medium cursor-pointer" onClick={() => fileInputRef.current?.click()}>click to upload</span></p>
            <p className="text-xs text-gray-300 mt-0.5">PNG, JPG, PDF, DOCX up to 50MB</p>
          </div>

          {/* Files */}
          {viewMode === 'list' ? (
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="grid grid-cols-12 px-4 py-2.5 border-b border-gray-100 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                <div className="col-span-5">Name</div>
                <div className="col-span-2">Size</div>
                <div className="col-span-2">Uploaded</div>
                <div className="col-span-2">By</div>
                <div className="col-span-1"></div>
              </div>
              {filteredFiles.length === 0 ? (
                <div className="py-10 text-center text-sm text-gray-400">No files found</div>
              ) : (
                filteredFiles.map(file => {
                  const Icon = typeIcons[file.type] || File;
                  return (
                    <div key={file.id} className="grid grid-cols-12 px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors items-center">
                      <div className="col-span-5 flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${typeColors[file.type]}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-900 truncate max-w-[200px]">{file.name}</p>
                          <div className="flex gap-1 mt-0.5">
                            {file.tags.slice(0, 2).map(tag => (
                              <span key={tag} className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">{tag}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="col-span-2 text-xs text-gray-500">{file.size}</div>
                      <div className="col-span-2 text-xs text-gray-500">{file.uploadedAt}</div>
                      <div className="col-span-2 text-xs text-gray-500 truncate">{file.uploadedBy}</div>
                      <div className="col-span-1 flex items-center gap-1 justify-end">
                        <button className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600">
                          <Download className="w-3.5 h-3.5" />
                        </button>
                        <button className="p-1.5 rounded-lg hover:bg-red-50 transition-colors text-gray-400 hover:text-red-500">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {filteredFiles.map(file => {
                const Icon = typeIcons[file.type] || File;
                return (
                  <div key={file.id} className="bg-white rounded-2xl border border-gray-200 p-4 hover:shadow-sm transition-shadow">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${typeColors[file.type]}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <p className="text-xs font-medium text-gray-900 truncate">{file.name}</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">{file.size} · {file.uploadedAt}</p>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex gap-1">
                        {file.tags.slice(0, 1).map(t => <span key={t} className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">{t}</span>)}
                      </div>
                      <div className="flex gap-1">
                        <button className="p-1 rounded-lg hover:bg-gray-100 transition-colors text-gray-400"><Download className="w-3.5 h-3.5" /></button>
                        <button className="p-1 rounded-lg hover:bg-red-50 transition-colors text-gray-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
